import "amazon-connect-streams";
import { getLogger } from "@expert/logging";
import { immerable } from "immer";

import type {
    AgentActivity,
    CallDirection,
    ConferenceParticipant,
    HoldState,
    IVRInfo,
    Locale,
    OutboundCallKind,
    VoiceTask,
    Whisper,
} from "@/sdk";

import { tfnToMaskedName } from "@/shared-utils";

import AmazonConnectContact, { type AmazonConnectContactConfig } from "../AmazonConnectContact";
import contactStateTypeToTaskStatus from "./contactStateTypeToTaskStatus";
import { contactToParticipants } from "./contactToParticipants";
import hasCustomerLeft from "./hasCustomerLeft";

function parsePositiveNumber(value: string | undefined): number | undefined {
    if (!value) return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

export default class AmazonConnectVoiceContact extends AmazonConnectContact implements VoiceTask {
    override [immerable] = true;

    readonly agentCallId: string | undefined;
    readonly customerCallId: string | undefined;
    readonly mdn: string;
    readonly callerId: string;
    readonly whisperTTS: Whisper | undefined;
    callDirection: CallDirection;
    isMuted: boolean;
    holdState: HoldState;
    conferenceStarted: boolean;
    customerLeftTheConference: boolean;
    conferenceParticipants: ConferenceParticipant[] = [];
    ivrInfo: IVRInfo | undefined;
    public readonly partner: string;
    public readonly client: string | undefined;

    constructor(
        name: string,
        config: AmazonConnectContactConfig,
        mdn: string | undefined,
        callerId: string | undefined,
        locale: Locale | undefined,
        partner: string,
        client: string | undefined,
    ) {
        super(name, config, locale);
        this.partner = partner;
        this.client = client;
        const { contact } = config;
        const agentConnection = contact.getAgentConnection() as connect.VoiceConnection;
        const initialConnection = contact.getInitialConnection();

        this.status = contactStateTypeToTaskStatus(contact.getState().type);
        this.customerCallId = initialConnection.contactId;
        this.agentCallId = agentConnection.contactId;
        this.mdn = mdn ?? initialConnection.getEndpoint().phoneNumber;
        this.callerId = callerId ?? initialConnection.getEndpoint().phoneNumber;
        this.callDirection = contact.isInbound() ? "inbound" : "outbound";
        this.isMuted = agentConnection.isMute();
        this.holdState = initialConnection.isOnHold()
            ? { onHold: true, onHoldSince: initialConnection.getState().timestamp.getTime() }
            : { onHold: false, onHoldSince: null };
        this.conferenceStarted = contact.getActiveConnections().length > 2;
        this.customerLeftTheConference = hasCustomerLeft(contact);
        this.conferenceParticipants = contactToParticipants(contact);
        this.assignedAt = new Date();

        const contactAttributes = contact.getAttributes();
        this.ivrInfo = {
            summary: contactAttributes.summary?.value,
            intent: contactAttributes.intent?.value,
            email: contactAttributes.email?.value,
            status: contactAttributes.status?.value,
            customerName: contactAttributes.customerName?.value,
            device: contactAttributes.device?.value,
            orderNumber: contactAttributes.orderNumber?.value,
            productLink: contactAttributes.productLink?.value,
            productTitle: contactAttributes.productTitle?.value,
            brandPhoneNumber: contactAttributes.brandPhoneNumber?.value,
            supportPhoneNumber: contactAttributes.supportPhoneNumber?.value,
            conversationalSummary: contactAttributes.conversationalSummary?.value,
            accountId: contactAttributes.accountId?.value,
            warmTransferMessage: contactAttributes.warmTransferMessage?.value,
            isWarmHandoffEnabled: contactAttributes.isWarmHandoffEnabled?.value,
            expertScript: contactAttributes.expertScript?.value,
            handoffRecordingRuntimeMs: parsePositiveNumber(contactAttributes.handoffRecordingRuntimeMs?.value),
            interactionId: contactAttributes.INTERACTION_ID?.value,
            enableVmpSales: contactAttributes.ENABLE_VMP_SALES?.value,
        };

        this.whisperTTS = contactAttributes?.WHISPER?.value;
    }

    get supportNumberCalled(): string | undefined {
        // todo: implement
        return undefined;
    }

    get lineOfBusiness(): string | undefined {
        // todo: implement
        return undefined;
    }

    override readonly hasCustomer = true;

    // todo: confirm this is right
    get conferenceId(): string | undefined {
        return this.config.id;
    }

    // todo: implement
    get asurionCallId(): string | undefined {
        return undefined;
    }

    // todo: implement
    readonly clientFriendlyName: string | undefined = undefined;

    // todo: implement
    get customerOriginalCallId(): string | undefined {
        return undefined;
    }

    // todo: implement
    get conversations(): Record<string, string> | undefined {
        return undefined;
    }

    get outboundKind(): OutboundCallKind | null {
        const { contact } = this.config;
        if (contact.isInbound()) return null;
        if (contact.getType() === connect.ContactType.QUEUE_CALLBACK) return "CallbackNow";
        return "AdHocOutboundCall";
    }

    public override isAgentBusy(_agentActivity: AgentActivity): boolean {
        return ["assigned", "wrapping", "pending"].includes(this.status);
    }

    // todo: implement
    get userToUser(): string | undefined {
        throw new Error("Method not implemented.");
    }

    // todo: implement
    get whisper(): Whisper | undefined {
        return this.whisperTTS;
    }

    get routingQueue(): string {
        const logger = getLogger({ module: "AmazonConnectVoiceContact" });
        const { contact } = this.config;
        try {
            const queueInfo = contact.getQueue();
            return queueInfo?.name;
        } catch (error) {
            logger.warn({ contact, error }, "Unable to find queue name or info for contact");
            return "Unknown";
        }
    }

    override toLog(): Record<string, unknown> {
        return {
            ...super.toLog(),
            conferenceId: this.conferenceId,
            agentCallId: this.agentCallId,
            customerCallId: this.customerCallId,
            asurionCallId: this.asurionCallId,
            callDirection: this.callDirection,
            isMuted: this.isMuted,
            holdState: this.holdState,
            conferenceStarted: this.conferenceStarted,
            customerLeftTheConference: this.customerLeftTheConference,
            conferenceParticipants: this.conferenceParticipants.map(({ participantName, ...rest }) => ({
                participantName: tfnToMaskedName(participantName),
                ...rest,
            })),
        };
    }
}
